/**
 * Session routing helpers — decide whether to open a session inline or
 * on a different subdomain based on its agent_slug.
 *
 * The agent → sub-app-host mapping is CONFIG-DRIVEN: each agent's manifest
 * (`subdomain:`) declares the single DNS label of
 * its own UI host (e.g. protocols → "alg"); GET /config serves it on the
 * `agents` block and `agentSubdomain` below resolves it from the config
 * store. A NEW sub-app therefore needs no frontend release for routing —
 * only its YAML + infra hosting.
 *
 * Sub-app DETECTION and the strip-to-main-host step derive from THIS app's own
 * subdomain via deriveMainHost — DNS hierarchy IS the directory, so there is no
 * enumerated "list of all sub-app subdomains" to maintain (an incomplete list,
 * missing one app's prefix, produced a nested-host bug). Env vars cover only what
 * config and DNS can't:
 *   - VITE_<APP>_SUBDOMAIN — first-party fallback prefixes for the
 *     window before /config loads (and older backends without the field)
 *
 * Local vite-dev PORTS are not named here at all. They are discovered from each
 * apps/<name>/vite.config.ts at build time and injected (frontend/devPortMap.ts,
 * frontend/vite.config.ts); this file reads them from @/lib/devPorts, which is
 * empty in production. Two hand-written tables used to hold them and had
 * already drifted from apps/ and from each other.
 *
 * The compatibility app is served at `compatibility.{env}.tricorder.icu` (and
 * the usb pilot alias `compatibility.mvp.tricorder.icu`); its sessions carry
 * agent_slug "compatibility". The old `yc.` subdomain was retired in infra
 * (cloudflare.tf: "yc is retired — its site + DNS destroyed"), so the prefix
 * MUST be "compatibility." to match the live DNS, not the dead "yc.".
 */

import { deriveMainHost } from '@/api/mainAppUrl'
import { MAIN_DEV_PORT, DEV_PORT_BY_PREFIX, SUB_APP_DEV_PORTS } from '@/lib/devPorts'
import type { SessionResponse } from '@/api/types_session'
import { agentEntryPathForSession } from '@/components/agentDetail/agentSessionEntry'
import { useConfigStore } from '@/stores/config'

const COMPAT_SUBDOMAIN = import.meta.env.VITE_COMPAT_SUBDOMAIN || 'compatibility.'
// The Studio (tricorder-studio-web, its own service at `alg.{env}`). Sessions
// there carry agent_slug "protocols".
const ALG_SUBDOMAIN = import.meta.env.VITE_ALG_SUBDOMAIN || 'alg.'
// Learn app (apps/learn, served at `learn.{env}`; dev port 5182 per
// apps/learn/vite.config.ts, mirrored in mainAppUrl.ts DEV_PORT_MAP).
// Config-only routing like patient-admin/access (no ENV_PREFIX_BY_SLUG entry —
// see the KNOWN GAP note below): the pre-/config cold-load window self-heals
// on the next click.
const LEARN_SUBDOMAIN = import.meta.env.VITE_LEARN_SUBDOMAIN || 'learn.'

// Slugs the MAIN APP itself has to name. Nothing more.
//
// This was once a constant per agent — a platform-side list of which agents
// exist, so adding or renaming one meant editing platform code. That list is
// gone: an agent's slug is its own bundle's business and lives in
// `apps/<app>/src/agent.ts`, beside the face and board it identifies.
//
// A slug earns a line here ONLY when this file, or another main-app module,
// names that agent for a reason of its own. Each below says which:
//
//   compatibility, protocols — the ENV_PREFIX_BY_SLUG cold-load fallback, plus
//     Home's launch routing.
//   roster  — Home renders a roster session's preview from the team's shift
//     rows (GET /shifts) and keeps the agent out of the generic launch path.
//   learn   — learnComponentRunUrl here, which TrainingHandoffCard uses to hand
//     a quiz run over to the learn app from inside a chat on another origin.
//     (Which of learn's SESSIONS open in that runner is no longer here: the
//     agent declares it in apps/learn/src/sessionEntry.ts.)
//   chat, handover, allocation — main-app agents. They have no apps/ directory
//     to own their slug, so this file IS their agent.ts.
//
// An agent whose ONLY namer is its own app does NOT belong here. access,
// team-access, mentor, knowledge-profile and patient-admin each had a constant
// that nothing outside their own bundle read; they now import AGENT_SLUG from
// their app. Adding a satellite's slug back is a regression — routing already
// resolves it from GET /config `agents[].subdomain` with no code change at all.
export const COMPATIBILITY_AGENT_SLUG = 'compatibility'
// Sessions spawned to run a protocol in the alg app carry this slug.
export const PROTOCOLS_AGENT_SLUG = 'protocols'
// Shift planner (apps/roster) — ward-level teamwork (Team scope, with allocation).
export const ROSTER_AGENT_SLUG = 'roster'
// Learn (apps/learn) — the conversational learning-guide agent; its quiz runs
// spawn as CHILD sessions on this same slug (the training_handoff handoff below),
// mirroring how protocols runs spawn on PROTOCOLS_AGENT_SLUG.
export const LEARN_AGENT_SLUG = 'learn'
// The default, main-app conversational agent. A session with this slug (or no
// slug at all — see agentKey) opens inline and is created via the deferred
// '/sessions/new' navigation rather than an eager POST. One constant so the
// new-session picker's chat special-case doesn't scatter the literal.
export const CHAT_AGENT_SLUG = 'chat'

/** The handover agent — moderates the co-signed shift-handover Rapport. */
export const HANDOVER_AGENT_SLUG = 'handover'
export const ALLOCATION_AGENT_SLUG = 'allocation'
// The report agent — drafts the care report from the patient's record and
// proposes the coding. A conversation, like handover, so the shared chat view
// is its face. It is named here for the same reason chat and handover are: it
// declares no `subdomain:` in its manifest, so it has no apps/
// directory to own its slug and the main app IS its surface.
//
// It had no face at all until 2026-08-19, which is not a cosmetic gap: an agent
// the picker offers but that resolves no face falls through navigateToSession
// to `/sessions/<id>`, a SIBLING route of `/home/*` in App.tsx, so it renders
// outside Home's pane — a bare full-screen chat with no rail, no twin and no
// Back. That is the same defect PR #2402 fixed for `learn` by giving it a face.
// agentFaces.test.ts now asserts every launchable agent resolves one, so the
// next agent declared before its surface exists fails there instead of in
// front of a nurse.
export const REPORT_AGENT_SLUG = 'report'


// Query param a returning child sub-app appends to the parent URL so the parent
// pulls the child's live summary. ONE constant shared by the producer
// (returnToParentUrl) and the consumer (Session.tsx) — a bare literal in both
// would let a rename in one silently kill the handoff (no card, no error).
export const FROM_CHILD_PARAM = 'from_child'

// Query param a LAUNCHER appends to a child sub-app URL carrying the absolute
// URL of the screen the run was launched from (Home workspace, full-screen
// chat, another sub-app). The child's return primitive (useSessionHandoff)
// reads it back — validated open-redirect-safe via getSafeCrossAppReturnTo —
// so "Return to chat" lands on the LAUNCH screen, not always the full-screen
// parent session. Absent/invalid → the legacy fallback (mainSessionUrl).
export const RETURN_TO_PARAM = 'return_to'

// Query param naming the PARENT session on a child's return to a screen whose
// URL doesn't carry the parent in its path (e.g. /home). Appended alongside
// FROM_CHILD_PARAM so the destination can record the child's summary and
// re-select the parent conversation. /sessions/:id consumers read the parent
// from the path and just strip this.
export const RETURN_PARENT_PARAM = 'parent_session'

// Query param that pre-assigns a NEW session to a patient. The "My patients"
// per-patient new-conversation button and the in-session "New patient" button
// both navigate to `/sessions/new?patient=<token>`; Session.tsx reads it and
// passes patient_id into POST /sessions so the session is created already
// assigned (backend: create-with-patient). ONE constant shared by every
// producer + the consumer so a rename can't silently break the assignment.
export const PATIENT_PARAM = 'patient'

/** What sessionSurfaceSlug reads: the pinned agent's slug, when there is one. */
export type SurfacedSession = { agent?: { slug: string } }

/**
 * The agent whose surface shows this session: the agent it pins, or, for a
 * session that pins none, the protocols agent. A session without an agent is
 * a protocol run (tricorder-session/ADR-0009), and its screen is the
 * protocols agent's run view (tricorder-studio-web's /sessions/<id>). The one place that
 * decision is made, so a list, a route and a label cannot disagree on it.
 */
export function sessionSurfaceSlug(session: SurfacedSession): string {
  return session.agent ? session.agent.slug : PROTOCOLS_AGENT_SLUG
}

// Server-derived session classifications (SessionResponse.session_kind, from
// api-service-go's deriveSessionKind — a client cannot tell them apart itself).

// First-party fallback prefixes by slug — used only for the window before
// /config has loaded (and for older backends whose agents block omits
// `subdomain`). All values are env-sourced above, not behavior forks; new
// agents never need an entry here (the config path covers them).
//
// KNOWN GAP for that window: a future config-only agent (YAML subdomain, no
// entry here) resolves to null until /config lands, so a session card clicked
// in that first moment routes inline to the main app instead of its sub-app.
// Self-healing (the next click routes correctly) and currently unreachable —
// session lists render from the same API the config loads alongside — but if
// a fourth sub-app ever needs cold-load routing too, give it a VITE_*_SUBDOMAIN
// env fallback and an entry below, mirroring the three first-party apps.
const ENV_PREFIX_BY_SLUG: Record<string, string> = {
  [COMPATIBILITY_AGENT_SLUG]: COMPAT_SUBDOMAIN,
  [PROTOCOLS_AGENT_SLUG]: ALG_SUBDOMAIN,
}

/**
 * Resolve the sub-app subdomain PREFIX ("alg.") for an agent slug, or null
 * for a main-app agent. Config-driven first (GET /config `agents[].subdomain`
 * — the YAML-declared label, dot appended here), env fallback second.
 * Non-hook by design: callers include plain functions (navigateToSession);
 * zustand state is read imperatively.
 */
export function agentSubdomain(slug: string): string | null {
  const fromConfig = useConfigStore
    .getState()
    .config?.agents?.find((a) => a.slug === slug)?.subdomain
  if (fromConfig) return `${fromConfig}.`
  return ENV_PREFIX_BY_SLUG[slug] ?? null
}

/**
 * Resolve the MAIN-app host + port from the current location: strip any sub-app
 * subdomain prefix and map any sub-app dev port back to the main port. Every URL
 * builder below derives from THIS, so each works whether called from the main
 * app OR another sub-app — e.g. the cross-app "return to parent" path, where a
 * Y-site child may target a parent on another app's host. (Building naively
 * from the current host would nest the two prefixes + use the wrong port.)
 */
function mainHostParts(): { protocol: string; mainHost: string; mainPort: string } {
  const { protocol, hostname, port } = window.location
  // Strip THIS app's own subdomain to reach the parent (twin) host. DNS
  // hierarchy IS the directory: each sub-app build injects its own label
  // (VITE_APP_SUBDOMAIN), so the parent is "current host minus my label" — no
  // enumerated list of sibling apps to maintain or let drift. Shared with
  // baseUrl.ts (parent API host) and mainAppUrl.ts (parent twin app). Building
  // naively from the current host would nest one app prefix under another (the bug this fixed:
  // the old prefix list never carried `knowledge-profile.`, so a knowledge-profile-app hop prepended
  // onto the full host).
  const mainHost = deriveMainHost(hostname)
  const mainPort = SUB_APP_DEV_PORTS.has(port) ? MAIN_DEV_PORT : port
  return { protocol, mainHost, mainPort }
}

// Local vite-dev port for a target sub-app prefix. Outside the standalone
// vite-dev setup (prod, or the single-port local nginx) every app shares the
// main port, so this is a no-op there.
function subAppPort(prefix: string, mainPort: string): string {
  if (mainPort !== MAIN_DEV_PORT) return mainPort
  return DEV_PORT_BY_PREFIX[prefix] ?? mainPort
}

// scheme://[prefix]host[:port] for a sub-app, derived from the current location
// (mainHostParts): strip our own subdomain to reach the parent host, re-prefix
// the target's, and map the main vite-dev port to the target's. Shared by every
// sub-app URL builder below so the host/port derivation lives in one place.
export function subAppOrigin(prefix: string): string {
  const { protocol, mainHost, mainPort } = mainHostParts()
  const port = subAppPort(prefix, mainPort)
  const portSuffix = port ? `:${port}` : ''
  return `${protocol}//${prefix}${mainHost}${portSuffix}`
}

// Serialize params to a query string. encodeURIComponent (not URLSearchParams)
// keeps the historical wire format: %20 for spaces, never the form-encoded '+'.
export function toQueryString(params?: Record<string, string>): string {
  if (!params) return ''
  const pairs = Object.entries(params).map(
    ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
  )
  return pairs.length ? `?${pairs.join('&')}` : ''
}

// One URL builder for every sub-app SESSION: stay on the subdomain if already
// there, otherwise prefix the main host; map the main vite-dev port to the app's.
function subAppSessionUrl(
  prefix: string,
  sessionId: string,
  params?: Record<string, string>,
): string {
  return `${subAppOrigin(prefix)}/sessions/${sessionId}${toQueryString(params)}`
}

// subAppRootUrl (the session-LESS '/' entry of a sub-app) went with
// algCatalogUrl on 2026-08-16 — it had exactly that one caller. The
// launch-then-defer flow it served is now in-shell: Home renders the agent's
// face in the frame and the agent mints its own run session when the nurse
// commits, so there is no origin to leave and nothing to frame via ?patient=.

/**
 * Builds the sub-app session URL for an agent slug, or null for a main-app
 * agent. The generic entry point — navigateToSession and any caller that
 * needs an agent's session URL without hardcoding its host go through this.
 */
export function agentSessionUrl(
  slug: string,
  sessionId: string,
  params?: Record<string, string>,
): string | null {
  const prefix = agentSubdomain(slug)
  return prefix ? subAppSessionUrl(prefix, sessionId, params) : null
}

/**
 * The agent's full-screen surface — its own sub-app — at `path`, the agent's
 * OWN path (`/studio/<id>/v1`, not `/home/a/<slug>/…`), or null for an agent
 * the main app is the only surface of. Keyed on the slug, like every other
 * builder here: the subdomain is what the agent declares, never its apps/
 * directory name.
 */
export function agentFullScreenUrl(slug: string, path: string): string | null {
  const prefix = agentSubdomain(slug)
  if (!prefix) return null
  const normalisedPath = path.startsWith('/') ? path : `/${path}`
  return `${subAppOrigin(prefix)}${normalisedPath}`
}

// algProtocolRunUrl and algCatalogUrl lived here until 2026-08-16. Both built
// cross-origin URLs to alg.<tenant>, and both were the reason a protocol threw
// the app shell away: tapping a handoff left the origin, so the nurse lost the
// twin, the rail and Back mid-protocol. Both surfaces are in-shell paths now:
// an agent's offer of a run links /home/a/protocols/runs/new?protocol_id=…&
// version=…, and Home shows the protocols agent's app (tricorder-studio-web)
// in the pane at that path (RemoteAgentFace), which replaces it with
// /home/a/protocols/runs/<session>. Deleted rather than left unused: an
// exported URL builder that still works is an invitation to link out again.
// The learn app keeps its own builders below because its handoff is still a
// cross-origin hop.

/** The learn app's own origin — the one place the three learn URL builders
 *  below resolve its host, so they cannot drift apart. */
function learnOrigin(): string {
  return subAppOrigin(agentSubdomain(LEARN_AGENT_SLUG) ?? LEARN_SUBDOMAIN)
}

/**
 * Builds the learn-app URL that runs training component `componentId`@`version`
 * inside the child session `childSessionId` — apps/learn's resume route
 * (`/runs/:id?component=&version=`, see apps/learn/src/pages/Run.tsx). The
 * child session already exists (spawned under the parent chat by the
 * training_handoff handoff card), and the query params tell the runner which
 * component version to load. The child stays live (never archived); the
 * parent chat pulls a grounded summary of it LIVE via GET /sessions/{id}/summary
 * when reopened with ?from_child=<id> — same pull contract as algProtocolRunUrl.
 *
 * `returnTo` (the launcher's current absolute URL) rides along as
 * RETURN_TO_PARAM so the run's "Return to chat" comes back to the screen the
 * nurse launched from.
 */
export function learnComponentRunUrl(
  childSessionId: string,
  componentId: string,
  componentVersion: string,
  returnTo?: string,
): string {
  const params: Record<string, string> = { component: componentId, version: componentVersion }
  if (returnTo) params[RETURN_TO_PARAM] = returnTo
  // `/runs/:id`, not `/sessions/:id` — apps/learn keeps the quiz-run route
  // distinct from its ordinary chat session route (see App.tsx) so the two
  // very different session kinds (a run vs a conversation) that both carry
  // agent_slug=learn never collide on one path.
  return `${learnOrigin()}/runs/${childSessionId}${toQueryString(params)}`
}

/**
 * Builds the MAIN-app URL for a session id — strips a sub-app subdomain
 * prefix if present and maps a sub-app dev port back to the main port. Used to
 * navigate FROM a sub-app (e.g. a compatibility child session) back to a parent
 * that lives on the main app.
 */
export function mainSessionUrl(sessionId: string): string {
  const { protocol, mainHost, mainPort } = mainHostParts()
  const portSuffix = mainPort ? `:${mainPort}` : ''
  return `${protocol}//${mainHost}${portSuffix}/sessions/${sessionId}`
}

// True if the current page is served from a sub-app. The subdomain prefix is the
// primary signal (prod + subdomain-mapped local hosts). The dev-port check is a
// fallback for the bare-`localhost:<sub-app-port>` dev setup (no subdomain). KNOWN
// edge: if the MAIN app is misconfigured to run on a sub-app dev port, this would
// false-positive — acceptable, since the main app never uses those ports.
function onSubApp(): boolean {
  const { hostname, port } = window.location
  // On a sub-app iff stripping this app's own subdomain changes the host (prod
  // subdomain hosts), or we're on a sub-app vite-dev port (bare-localhost dev,
  // which has no subdomain to strip).
  return deriveMainHost(hostname) !== hostname || SUB_APP_DEV_PORTS.has(port)
}

/**
 * Navigate to a session, choosing the right destination based on its agent.
 * An agent with a sub-app subdomain (config-driven, see agentSubdomain) is a
 * full-page navigation to its host; a main-app session navigates inline —
 * UNLESS we're currently on a sub-app (e.g. a compatibility child returning
 * to its main-app parent), in which case it's a cross-app full-page
 * navigation to the main URL.
 */
export function navigateToSession(
  session: Pick<SessionResponse, 'id' | 'agent'>,
  inlineNavigate: (path: string) => void,
): void {
  // Every session is a conversation from the cut-over on; an agent that wants
  // one opened off its face says so (apps/<slug>/src/sessionEntry.ts).
  const entryPath = agentEntryPathForSession(session)
  if (entryPath !== '/') {
    // `<slug>.` is the fallback prefix when /config has not landed yet, which
    // is what the per-agent learnOrigin() helper here used to hardcode. Same
    // KNOWN GAP as agentSubdomain's, now stated once for every agent instead
    // of once per agent: a tenant that renames the subdomain (lernen.) routes
    // correctly the moment config arrives.
    const slug = sessionSurfaceSlug(session)
    window.location.href = `${subAppOrigin(agentSubdomain(slug) ?? `${slug}.`)}${entryPath}`
    return
  }
  const subAppUrl = agentSessionUrl(sessionSurfaceSlug(session), session.id)
  if (subAppUrl) {
    window.location.href = subAppUrl
    return
  }
  if (onSubApp()) {
    window.location.href = mainSessionUrl(session.id)
    return
  }
  inlineNavigate(`/sessions/${session.id}`)
}
